import { Button, Checkbox, Select, SelectItem, Spinner } from "@heroui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, format, parseISO, startOfDay } from "date-fns";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

import PageHeader from "@/components/common/PageHeader";
import SingleFieldEditDialog, {
  type SingleFieldTarget,
} from "@/components/common/SingleFieldEditDialog";
import { listPartners } from "@/features/partners/api";
import { updateProject, updateProjectItem } from "@/features/projects/api";
import { errorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { canWrite } from "@/lib/authorization";
import { showError, showSuccess } from "@/lib/toast";

import { entryKey, listShippingCalendar, type ShippingCalendarEntry } from "./api";
import MonthGrid from "./MonthGrid";
import { dateKey, monthLabel, shiftMonth } from "./calendarDates";
import {
  currentMonthKey,
  useShippingCalendarParams,
} from "./useShippingCalendarParams";

/** 「未設定」を表す Select の内部キー（空文字は HeroUI が扱えない）。 */
const ALL_CLIENTS = "__all__";

/**
 * 出荷カレンダー。1 か月ぶんの出荷予定を日ごとに並べ、ドラッグで日を付け替える。
 *
 * 日付は**明細の出荷予定日、無ければ案件の出荷予定日**という実効日で決まる
 * （[[listShippingCalendar]]）。分納の案件は複数の日に出るので、1 枚のカードは
 * 「案件 × その日に出る明細」を表す。
 *
 * 表示条件（月・得意先・未出荷のみ）は URL のクエリに持つので、画面をそのまま
 * 人に渡せる。表示列の設定のような localStorage は持たない（並べるのは表では
 * なくカードなので、出す項目を選ぶ余地がまだ無い）。
 */
export default function ShippingCalendar() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const editable = canWrite(user, "transactional");
  const { params, update, days, dateFrom, dateTo, shippedParam } =
    useShippingCalendarParams();

  /** いま掴んでいるカード。dragover 中は dataTransfer を読めないので state に持つ。 */
  const [dragging, setDragging] = useState<ShippingCalendarEntry | null>(null);
  /** ハイライトしている落とし先の日。 */
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  /** 付け替えの送信中のカード（二重に動かさないため）。 */
  const [movingKeys, setMovingKeys] = useState<Set<string>>(new Set());
  /** ケバブから日付を直接入れるダイアログの対象。 */
  const [editing, setEditing] = useState<ShippingCalendarEntry | null>(null);

  const query = useQuery({
    queryKey: ["shipping-calendar", { dateFrom, dateTo, params }],
    queryFn: () =>
      listShippingCalendar({
        dateFrom,
        dateTo,
        clientId: params.clientId ?? undefined,
        shipped: shippedParam,
      }),
  });

  // 得意先は**取引中のものだけ**を選択肢に出す（取引を止めた相手を選ばせない）。
  const partnersQuery = useQuery({
    queryKey: ["partners", "picker"],
    queryFn: () =>
      listPartners({ disabled: false, isClient: true, limit: 500, offset: 0 }),
  });

  /** 日付ごとにカードを束ねる。マスは画面側で作るので、API は平らに返している。 */
  const entriesByDate = useMemo(() => {
    const map = new Map<string, ShippingCalendarEntry[]>();
    for (const entry of query.data?.entries ?? []) {
      const list = map.get(entry.ship_date);
      if (list) list.push(entry);
      else map.set(entry.ship_date, [entry]);
    }
    return map;
  }, [query.data]);

  // 「遅れ」の判定に使う今日の 0 時。描画のたびに作り直さないよう固定する。
  const today = useMemo(() => startOfDay(new Date()), []);

  const move = useMutation({
    mutationFn: async ({
      entry,
      date,
    }: {
      entry: ShippingCalendarEntry;
      date: string | null;
      /** 失敗をトーストで出さない（ダイアログがその場に出すとき）。 */
      quiet?: boolean;
    }) => {
      // 案件の日で出ているカードは案件側を直す（自分の日を持たない明細がまとめて
      // 動く）。明細が自分の日を持つカードは、その明細だけを直す。
      if (entry.date_source === "project") {
        await updateProject(entry.project_id, { shipping_planned_date: date });
        return;
      }
      // 一括で直す口は無いので 1 行ずつ。普通は 1〜数行なので待ち時間は短い。
      // 途中で失敗したら部分的に動いた状態になるが、どちらにせよ引き直して
      // 実際の状態を描くので、画面とサーバがずれたままにはならない。
      for (const itemId of entry.project_item_ids) {
        await updateProjectItem(entry.project_id, itemId, {
          shipping_planned_date: date,
        });
      }
    },
    onMutate: ({ entry }) => {
      setMovingKeys((prev) => new Set(prev).add(entryKey(entry)));
    },
    onSuccess: (_data, { entry, date }) => {
      showSuccess(
        date
          ? `${entry.project_number} の出荷予定日を ${format(parseISO(date), "M/d")} にしました。`
          : `${entry.project_number} の出荷予定日を未設定にしました。`,
      );
    },
    onError: (e, { quiet }) => {
      // ダイアログから保存したときは、理由をダイアログの中に出す（閉じずに
      // 直せるようにするため）。トーストと二重に出さない。
      if (!quiet) showError(errorMessage(e, "出荷予定日の変更に失敗しました。"));
    },
    onSettled: (_data, _error, { entry }) => {
      setMovingKeys((prev) => {
        const next = new Set(prev);
        next.delete(entryKey(entry));
        return next;
      });
      // 成否どちらでも引き直す（楽観更新はしない）。案件一覧・詳細にも同じ日付が
      // 出ているので、そちらも一緒に無効にする。
      void queryClient.invalidateQueries({ queryKey: ["shipping-calendar"] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
      void queryClient.invalidateQueries({ queryKey: ["project", entry.project_id] });
      void queryClient.invalidateQueries({
        queryKey: ["project-items", entry.project_id],
      });
    },
  });

  const moveTo = (entry: ShippingCalendarEntry, date: string | null) => {
    if (!editable || entry.shipped_at != null) return;
    if (date === entry.ship_date) return;
    move.mutate({ entry, date });
  };

  const clearDrag = () => {
    setDragging(null);
    setDropTarget(null);
  };

  const editTarget: SingleFieldTarget | null = editing
    ? {
        label: "出荷予定日",
        value: editing.ship_date,
        kind: "date",
        description:
          editing.date_source === "item"
            ? `${editing.project_number} のこの日の明細 ${editing.item_count} 行の出荷予定日を変えます。空にすると案件の出荷予定日に従います。`
            : `${editing.project_number} の出荷予定日を変えます。空にするとカレンダーから外れます。`,
      }
    : null;

  const isCurrentMonth = params.month === currentMonthKey();

  return (
    <div className="relative h-full">
      {/* 下にも余白を取る（詳細画面と同じ）。一覧は表が内側で切れるので上だけだが、
          カレンダーは枠ごと画面に収めるため、下が窓の縁に貼り付くと最終週が
          切れたように見える。 */}
      <div className="flex h-full flex-col px-6 pt-3 pb-3">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md">
          <PageHeader
            title="出荷カレンダー"
            actions={
              <>
                <div className="flex items-center gap-1">
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label="前の月"
                    onPress={() => update({ month: shiftMonth(params.month, -1) })}
                  >
                    <ChevronLeft size={18} />
                  </Button>
                  <span className="min-w-[7.5rem] text-center text-base font-bold tabular-nums">
                    {monthLabel(params.month)}
                  </span>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label="次の月"
                    onPress={() => update({ month: shiftMonth(params.month, 1) })}
                  >
                    <ChevronRight size={18} />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  radius="sm"
                  isDisabled={isCurrentMonth}
                  onPress={() => update({ month: currentMonthKey() })}
                >
                  今月
                </Button>
                <Select
                  aria-label="得意先で絞り込む"
                  placeholder="得意先"
                  size="sm"
                  variant="bordered"
                  radius="sm"
                  className="w-56"
                  selectedKeys={[
                    params.clientId == null ? ALL_CLIENTS : String(params.clientId),
                  ]}
                  disallowEmptySelection
                  onSelectionChange={(keys) => {
                    const key = [...keys][0] as string | undefined;
                    update({
                      clientId: !key || key === ALL_CLIENTS ? null : Number(key),
                    });
                  }}
                >
                  {[
                    { key: ALL_CLIENTS, label: "すべての得意先" },
                    ...(partnersQuery.data?.items ?? []).map((p) => ({
                      key: String(p.partner_id),
                      label: `${p.partner_code} ${p.partner_name}`,
                    })),
                  ].map((option) => (
                    <SelectItem key={option.key}>{option.label}</SelectItem>
                  ))}
                </Select>
                <Checkbox
                  size="sm"
                  isSelected={params.unshippedOnly}
                  onValueChange={(v) => update({ unshippedOnly: v })}
                >
                  <span className="text-sm">未出荷のみ</span>
                </Checkbox>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label="再読込"
                  onPress={() => void query.refetch()}
                >
                  <RefreshCw size={16} />
                </Button>
              </>
            }
          />
          {query.isError ? (
            <div className="p-6 text-sm text-danger">
              {errorMessage(query.error, "出荷カレンダーを取得できませんでした。")}
            </div>
          ) : (
            <MonthGrid
              month={params.month}
              days={days}
              entriesByDate={entriesByDate}
              today={today}
              editable={editable}
              dropTarget={dropTarget}
              movingKeys={movingKeys}
              onDragStart={setDragging}
              onDragEnd={clearDrag}
              onDragOver={(date) =>
                setDropTarget((prev) => (prev === date ? prev : date))
              }
              onDragLeave={(date) =>
                setDropTarget((prev) => (prev === date ? null : prev))
              }
              onDropOn={(date) => {
                const entry = dragging;
                clearDrag();
                if (entry) moveTo(entry, date);
              }}
              onMoveByDays={(entry, delta) =>
                moveTo(entry, dateKey(addDays(parseISO(entry.ship_date), delta)))
              }
              onEditDate={setEditing}
            />
          )}
        </section>
      </div>
      {/* 初回の取得中だけ覆う。月を送ったときは前の月を出したまま差し替える
          （毎回カレンダーが消えると、送った先が見えるまで何も読めない）。 */}
      {query.isPending && (
        <div className="absolute inset-0 grid place-items-center bg-white/60">
          <Spinner />
        </div>
      )}
      <SingleFieldEditDialog
        target={editTarget}
        onClose={() => setEditing(null)}
        onSave={async (value) => {
          const entry = editing;
          if (!entry) return null;
          try {
            await move.mutateAsync({ entry, date: value, quiet: true });
            return null;
          } catch (e) {
            return errorMessage(e, "保存に失敗しました。");
          }
        }}
      />
    </div>
  );
}
